import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, LifeBuoy, Menu, X, ArrowRight, LogIn, Sparkles } from "lucide-react";
import Brand from "@/components/site/Brand";
import Button from "@/components/site/Button";
import InstallChip from "@/components/pwa/InstallChip";

const contactChannels = [
  {
    key: "sales",
    label: "Sales",
    icon: Phone,
    value: "0161 524 1567",
    href: "tel:01615241567",
    testid: "nav-sales-phone",
    priority: 1,
  },
  {
    key: "email",
    label: "Email",
    icon: Mail,
    value: "sales@cannicomply.com",
    href: "mailto:sales@cannicomply.com",
    testid: "nav-sales-email",
    priority: 2, // hidden below md
  },
  {
    key: "support",
    label: "Support",
    icon: LifeBuoy,
    value: "support@ukcsl.co.uk",
    href: "mailto:support@ukcsl.co.uk",
    testid: "nav-support-email",
    priority: 1,
  },
];

const NAV_LINKS = [
  ["Platform", "#process"],
  ["Try it", "#tryit"],
  ["About", "#about"],
  ["Pricing", "#pricing"],
  ["Security", "#features"],
  ["FAQ", "#faq"],
  ["Contact", "#contact"],
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll + Esc-to-close while the mobile menu is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  return (
    <motion.header
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="site-nav"
    >
      {/* Announcement strip — enterprise contact channels */}
      <div
        className="bg-[#0B111F]/90 backdrop-blur-md border-b border-white/[0.04] text-slate-400"
        data-testid="nav-announcement-bar"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-5 lg:px-8 h-9 flex items-center justify-end lg:justify-between gap-4">
          <span className="hidden lg:inline text-[11px] truncate">
            The workforce assurance platform for regulated medical cannabis
            organisations
          </span>
          <ul className="flex items-center gap-x-4 sm:gap-x-6 shrink-0">
            {contactChannels.map((c, i) => {
              const Icon = c.icon;
              const hideOnMobile = c.priority === 2;
              return (
                <li
                  key={c.key}
                  className={`flex items-center ${
                    hideOnMobile ? "hidden md:flex" : "flex"
                  }`}
                >
                  <a
                    href={c.href}
                    data-testid={c.testid}
                    className="group inline-flex items-center gap-1.5 text-[11px] leading-none whitespace-nowrap hover:text-white transition-colors"
                  >
                    <Icon
                      size={11}
                      className="text-[#D4A65A] shrink-0"
                      aria-hidden="true"
                    />
                    <span className="hidden sm:inline text-slate-500 font-semibold">
                      {c.label}:
                    </span>
                    <span className="tabular-nums">{c.value}</span>
                  </a>
                  {i < contactChannels.length - 1 && (
                    <span
                      aria-hidden="true"
                      className={`hidden md:inline-block h-1 w-1 rounded-full bg-slate-700 ml-4 sm:ml-6 ${
                        hideOnMobile ? "md:hidden lg:inline-block" : ""
                      }`}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-3">
        <div
          className={`flex items-center justify-between rounded-xl px-4 md:px-5 py-3 transition-all duration-300 ${
            scrolled
              ? "backdrop-blur-xl bg-[#050B18]/85 border border-white/[0.08] shadow-[0_18px_50px_-25px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(212,166,90,0.08)]"
              : "backdrop-blur-md bg-[#050B18]/50 border border-white/[0.06]"
          }`}
        >
          <Brand />

          <nav className="hidden md:flex items-center gap-0.5 text-[13px] text-slate-300">
            {NAV_LINKS.map(([label, href]) => (
              <a
                key={label}
                href={href}
                data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3.5 py-2 rounded-md hover:text-white hover:bg-white/[0.05] transition-colors"
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-2.5">
            <a
              href="/app/login"
              data-testid="nav-sign-in"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] text-slate-300 hover:text-white transition-colors"
            >
              Sign in
            </a>
            <Button
              as="a"
              href="/app/login"
              variant="ghost"
              size="sm"
              data-testid="nav-try-demo"
              className="hidden md:inline-flex"
            >
              Try demo
            </Button>
            <Button
              as="a"
              href="#tryit"
              variant="primary"
              size="sm"
              data-testid="nav-start-trial"
              className="group hidden sm:inline-flex"
            >
              Try the assessment
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-transform group-hover:translate-x-0.5"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </Button>

            {/* Install chip — mobile only, hidden if already installed. */}
            <InstallChip testid="nav-install-chip" />

            {/* Mobile hamburger — visible below sm so Sign in + CTA also gain
                a reachable home on phones in portrait. */}
            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              className="sm:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-white bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.1] transition-colors"
            >
              {mobileOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu sheet — full-width dropdown with links, Sign in, CTAs */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="mobile-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="sm:hidden fixed inset-0 top-[calc(2.25rem+64px)] bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
              data-testid="nav-mobile-backdrop"
              aria-hidden="true"
            />
            <motion.div
              key="mobile-panel"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="sm:hidden mx-5 mt-2 rounded-2xl border border-white/[0.08] bg-[#050B18]/95 backdrop-blur-xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
              data-testid="nav-mobile-menu"
            >
              <div className="px-2 py-2">
                <ul>
                  {NAV_LINKS.map(([label, href]) => (
                    <li key={label}>
                      <a
                        href={href}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`nav-mobile-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg text-[14px] text-slate-200 hover:text-white hover:bg-white/[0.06] transition-colors"
                      >
                        <span>{label}</span>
                        <ArrowRight size={13} className="text-slate-500" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-4 py-4 border-t border-white/[0.06] bg-white/[0.02]">
                <div className="grid grid-cols-1 gap-2">
                  <a
                    href="/app/login"
                    onClick={() => setMobileOpen(false)}
                    data-testid="nav-mobile-sign-in"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-semibold text-white bg-white/[0.06] border border-white/[0.1] hover:bg-white/[0.1] transition-colors"
                  >
                    <LogIn size={14} />
                    Sign in
                  </a>
                  <a
                    href="/app/login"
                    onClick={() => setMobileOpen(false)}
                    data-testid="nav-mobile-try-demo"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-semibold text-slate-100 bg-transparent border border-white/[0.12] hover:bg-white/[0.06] transition-colors"
                  >
                    <Sparkles size={14} className="text-[#D4A65A]" />
                    Try demo
                  </a>
                  <a
                    href="#tryit"
                    onClick={() => setMobileOpen(false)}
                    data-testid="nav-mobile-start-trial"
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-[13.5px] font-semibold text-[#0B1220] bg-gradient-to-b from-[#E6BE7A] to-[#D4A65A] hover:from-[#F0D497] hover:to-[#D4A65A] transition-colors shadow-[0_8px_24px_-10px_rgba(212,166,90,0.6)]"
                  >
                    Try the assessment
                    <ArrowRight size={13} />
                  </a>
                </div>
                <div className="mt-4 pt-4 border-t border-white/[0.06] grid grid-cols-1 gap-1.5 text-[11.5px] text-slate-400">
                  <a
                    href="tel:01615241567"
                    className="inline-flex items-center gap-2 hover:text-white transition-colors"
                    data-testid="nav-mobile-sales-phone"
                  >
                    <Phone size={11} className="text-[#D4A65A]" />
                    <span className="text-slate-500 font-semibold">Sales:</span>
                    <span className="tabular-nums">0161 524 1567</span>
                  </a>
                  <a
                    href="mailto:support@ukcsl.co.uk"
                    className="inline-flex items-center gap-2 hover:text-white transition-colors"
                    data-testid="nav-mobile-support-email"
                  >
                    <LifeBuoy size={11} className="text-[#D4A65A]" />
                    <span className="text-slate-500 font-semibold">Support:</span>
                    <span>support@ukcsl.co.uk</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
